import type { CustomizationState, ThemeColors } from './types'

export const DEFAULT_STATE: CustomizationState = {
  theme: {},
  contentScale: 100,
  stylingPreset: 'default',
  position: {
    side: 'right',
    offset: {
      web: { horizontal: 16, vertical: 16 },
      mobile: { horizontal: 16, vertical: 16 }
    }
  },
  avatar: {
    hidden: false,
    position: 'side',
    size: 32
  },
  messageBubbles: {
    business: {
      bubbleMaxWidth: undefined,
      hideBubble: false
    },
    user: {
      bubbleMaxWidth: undefined,
      hideBubble: false
    }
  },
  conversationListSettings: {
    avatar: { hidden: false, size: 48 },
    hideTimestamp: false,
    hideMessagePreview: false,
    hideDivider: false,
    groupByTimestamp: false
  },
  settings: {
    hideHeader: false,
    conversationList: false,
    messageLog: false,
    hideNewConversationButton: false
  },
  locale: 'en-US',
  zIndex: 999999
}

export function applyCustomization(state: Partial<CustomizationState>) {
  if (typeof window.zE === 'undefined') {
    console.warn('❌ Zendesk widget not loaded yet')
    return
  }

  // ALWAYS send hideHeader value (true or false), matching test.html pattern
  const fullConfig: any = {
    common: {
      hideHeader: state.settings?.hideHeader || false
    }
  }

  console.log('=== APPLYING CUSTOMIZATION ===')
  console.log('Full config:', JSON.stringify(fullConfig, null, 2))

  try {
    window.zE('messenger:set', 'customization', fullConfig)
    console.log('✓ Customization applied successfully')
  } catch (e) {
    console.error('✗ Failed to apply customization:', e)
  }

  console.log('=== END CUSTOMIZATION ===\n')
}

export function generateCode(state: Partial<CustomizationState>): string {
  const config: any = {}

  if (state.theme) {
    config.theme = state.theme
  }

  if (state.contentScale !== undefined || state.stylingPreset !== undefined || state.settings?.hideHeader) {
    config.common = {
      contentScale: state.contentScale ?? 100,
      hideHeader: state.settings?.hideHeader || false
    }
    if (state.stylingPreset) {
      config.common.stylingPreset = state.stylingPreset
    }
  }

  if (state.conversationListSettings || state.settings) {
    config.conversationList = {
      hideHeader: state.settings?.conversationList && !state.settings?.hideHeader ? true : false,
      hideNewConversationButton: state.settings?.hideNewConversationButton || false,
      ...(state.conversationListSettings && {
        avatar: {
          hidden: state.conversationListSettings.avatar.hidden,
          size: state.conversationListSettings.avatar.size
        },
        hideTimestamp: state.conversationListSettings.hideTimestamp,
        hideMessagePreview: state.conversationListSettings.hideMessagePreview,
        hideDivider: state.conversationListSettings.hideDivider,
        groupByTimestamp: state.conversationListSettings.groupByTimestamp
      })
    }
  }

  if (state.avatar || state.messageBubbles || state.settings) {
    config.messageLog = {
      hideHeader: state.settings?.messageLog && !state.settings?.hideHeader ? true : false,
      ...(state.avatar && {
        avatar: {
          hidden: state.avatar.hidden,
          position: state.avatar.position,
          size: state.avatar.size
        }
      })
    }

    if (state.messageBubbles?.business.bubbleMaxWidth || state.messageBubbles?.business.hideBubble) {
      config.messageLog.businessMessage = {
        ...(state.messageBubbles.business.bubbleMaxWidth && { bubbleMaxWidth: state.messageBubbles.business.bubbleMaxWidth }),
        ...(state.messageBubbles.business.hideBubble && { hideBubble: true })
      }
    }

    if (state.messageBubbles?.user.bubbleMaxWidth || state.messageBubbles?.user.hideBubble) {
      config.messageLog.userMessage = {
        ...(state.messageBubbles.user.bubbleMaxWidth && { bubbleMaxWidth: state.messageBubbles.user.bubbleMaxWidth }),
        ...(state.messageBubbles.user.hideBubble && { hideBubble: true })
      }
    }
  }

  if (state.position) {
    config.position = {
      side: state.position.side,
      offset: {
        web: state.position.offset.web,
        mobile: state.position.offset.mobile
      }
    }
  }

  // Start with autorender configuration and embedded render
  let code = `// Configure autorender\nwindow.zEMessenger = {\n  autorender: false\n};\n\n`
  code += `// Render embedded widgets\nzE('messenger', 'render', {\n  mode: 'embedded',\n  conversationList: {\n    targetElement: '#widget_conversations'\n  },\n  messageLog: {\n    targetElement: '#widget_messages'\n  }\n});\n\n`

  // Add customization after a short delay
  code += `// Apply customization after widget renders\nsetTimeout(() => {\n  zE('messenger:set', 'customization', ${JSON.stringify(config, null, 2).replace(/\n/g, '\n  ')});\n`

  if (state.locale && state.locale !== 'en-US') {
    code += `\n  zE('messenger:set', 'locale', '${state.locale}');`
  }

  if (state.zIndex && state.zIndex !== 999999) {
    code += `\n  zE('messenger:set', 'zIndex', ${state.zIndex});`
  }

  code += `\n}, 1000);`

  return code
}

// Declare window.zE type
declare global {
  interface Window {
    zE?: any
    zEMessenger?: any
    hljs?: any
  }
}
